/** Art-space geometry shared by the avatar parts. Every part is drawn on a 120-unit canvas centred at 60. */

const VIEW_BOX_MIN = -14;
/** Width/height of the viewBox, in art units. Also the divisor for turning screen px into art units. */
export const VIEW_BOX_SIZE = 148;
/** Padding around the 120-unit figure leaves room for the thinking dots, sparkles and badge. */
export const VIEW_BOX = `${VIEW_BOX_MIN} ${VIEW_BOX_MIN} ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`;

/** Art units that render as `px` screen pixels in an avatar drawn `size` px wide. */
export const pxToArtUnits = (px: number, size: number): number => (px * VIEW_BOX_SIZE) / size;

/** A four-point sparkle star centred on (cx, cy). */
export const starPath = (cx: number, cy: number, r: number): string =>
	`M${cx} ${cy - r} Q${cx} ${cy} ${cx + r} ${cy} Q${cx} ${cy} ${cx} ${cy + r} Q${cx} ${cy} ${cx - r} ${cy} Q${cx} ${cy} ${cx} ${cy - r}Z`;

export interface ArtPoint {
	x: number;
	y: number;
}

/** Points evenly placed on a circle, from `fromDeg` to `toDeg` inclusive. */
export const pointsOnArc = (cx: number, cy: number, radius: number, fromDeg: number, toDeg: number, stepDeg: number): ArtPoint[] => {
	const points: ArtPoint[] = [];
	for (let deg = fromDeg; deg <= toDeg; deg += stepDeg) {
		const rad = (deg * Math.PI) / 180;
		points.push({ x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) });
	}
	return points;
};

/** The human face features (eyes, mouth, glasses) reuse the character's, shrunk around the nose. */
export const HUMAN_FACE_TRANSFORM = "translate(60 62) scale(.66) translate(-60 -62)";

/** Furthest the pupils travel toward a `gaze` target, in art units. Keeps a round pupil inside its sclera. */
export const GAZE_MAX_OFFSET = 3.2;

/** Art-space y of both faces' eye line (the human face scales around it, so it barely moves). */
const EYE_LINE_ART_Y = 60;
/** Where the eyes sit in the rendered avatar box, as a fraction of its height. */
export const EYE_LINE_FRACTION = (EYE_LINE_ART_Y - VIEW_BOX_MIN) / VIEW_BOX_SIZE;

/** The subset of `DOMRect` the gaze math needs, so it can be called with plain numbers. */
export interface AvatarRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

/** A screen-space direction (x right, y down). */
export interface GazeDirection {
	x: number;
	y: number;
}

/** Unit vector from the avatar's eye line to the target's centre; zero when the target sits right on the eyes. */
export const gazeToward = (from: AvatarRect, to: AvatarRect): GazeDirection => {
	const dx = to.left + to.width / 2 - (from.left + from.width / 2);
	const dy = to.top + to.height / 2 - (from.top + from.height * EYE_LINE_FRACTION);
	const distance = Math.hypot(dx, dy);
	return distance === 0 ? { x: 0, y: 0 } : { x: dx / distance, y: dy / distance };
};

/** Pupil offset in art units for a gaze direction; longer-than-unit directions are clamped to the max offset. */
export const gazeOffset = (gaze: GazeDirection): ArtPoint => {
	const length = Math.hypot(gaze.x, gaze.y);
	const scale = length > 1 ? GAZE_MAX_OFFSET / length : GAZE_MAX_OFFSET;
	return { x: gaze.x * scale, y: gaze.y * scale };
};

/** The screen side a pointing hand reaches toward. The avatar is never mirrored, so `Left` is always screen-left. */
export enum AssistantAvatarPointSide {
	Left = "left",
	Right = "right",
}
